import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useBot, useAskQuestion, useSources, useAllSources } from "../hooks/useApi";
import { useTranslation } from "../hooks/useTranslation";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ProductCardsGrid } from "../components/ProductCards";
import { StatoAssistente } from "../components/assistenti/SchedaAssistente";
import { BrandCard, BrandStyles, VARIABILI_SEZIONE } from "../components/integrations/brand";
import { BOTTONE, GRIGIO, SCURO, IntestazioneCasella } from "../components/integrations/casella";
import { AREA_TESTO } from "../components/widget/campi";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form";
import {
  ArrowLeft,
  Send,
  MessageSquare,
  Clock,
  ExternalLink,
  FileText,
  AlertTriangle,
  Trash2,
  BookmarkPlus,
  Loader2,
} from "lucide-react";

const QueryInterface = () => {
  const { t } = useTranslation();
  const { id: botId } = useParams();
  const { data: bot, isLoading: botLoading, error: botError } = useBot(botId);
  const askQuestion = useAskQuestion(botId);
  // Le fonti come le conta la Panoramica: il bot non porta con se' il numero.
  const { data: sourcesResponse } = useSources(botId);
  const { data: allSources } = useAllSources(botId);
  const fonti = Array.isArray(allSources)
    ? allSources.length
    : sourcesResponse?.pagination?.total_items ?? (sourcesResponse?.sources?.length || 0);

  const [queryHistory, setQueryHistory] = useState([]);
  const [savedQueries, setSavedQueries] = useState([]);

  const form = useForm({
    defaultValues: {
      question: "",
    },
  });

  // Load query history and saved queries from localStorage
  useEffect(() => {
    if (botId) {
      const historyKey = `query-history-${botId}`;
      const savedKey = `saved-queries-${botId}`;

      const history = localStorage.getItem(historyKey);
      const saved = localStorage.getItem(savedKey);

      if (history) {
        try {
          setQueryHistory(JSON.parse(history));
        } catch (e) {
          console.error("Failed to parse query history:", e);
        }
      }

      if (saved) {
        try {
          setSavedQueries(JSON.parse(saved));
        } catch (e) {
          console.error("Failed to parse saved queries:", e);
        }
      }
    }
  }, [botId]);

  // Save query history to localStorage
  const saveToHistory = (query, response) => {
    const newEntry = {
      id: Date.now().toString(),
      question: query,
      answer: response.answer,
      citations: response.citations || [],
      cards: response.cards || [], // Save product cards
      confidence: response.confidence,
      context: response.context,
      metadata: response.metadata,
      timestamp: new Date().toISOString(),
    };

    const updatedHistory = [newEntry, ...queryHistory].slice(0, 50); // Keep last 50 queries
    setQueryHistory(updatedHistory);

    const historyKey = `query-history-${botId}`;
    localStorage.setItem(historyKey, JSON.stringify(updatedHistory));
  };

  // Save query for later
  const saveQuery = (query, answer) => {
    const newSaved = {
      id: Date.now().toString(),
      question: query,
      answer: answer,
      timestamp: new Date().toISOString(),
    };

    const updatedSaved = [newSaved, ...savedQueries];
    setSavedQueries(updatedSaved);

    const savedKey = `saved-queries-${botId}`;
    localStorage.setItem(savedKey, JSON.stringify(updatedSaved));
  };

  // Remove from saved queries
  const removeSavedQuery = (queryId) => {
    const updatedSaved = savedQueries.filter((q) => q.id !== queryId);
    setSavedQueries(updatedSaved);

    const savedKey = `saved-queries-${botId}`;
    localStorage.setItem(savedKey, JSON.stringify(updatedSaved));
  };

  // Clear query history
  const clearHistory = () => {
    setQueryHistory([]);
    const historyKey = `query-history-${botId}`;
    localStorage.removeItem(historyKey);
  };

  const onSubmit = async (data) => {
    if (!data.question.trim()) return;

    try {
      const response = await askQuestion.mutateAsync({
        question: data.question,
      });
      saveToHistory(data.question, response);
      form.reset();
    } catch (error) {
      console.error("Query failed:", error);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return t("query.justNow");
    if (diffMinutes < 60) return t("query.minutesAgo", { count: diffMinutes });
    if (diffHours < 24) return t("query.hoursAgo", { count: diffHours });
    if (diffDays < 7) return t("query.daysAgo", { count: diffDays });
    return date.toLocaleDateString();
  };

  const getConfidenceColor = (confidence) => {
    if (confidence === "high") return "text-[#0F7A3D]";
    if (confidence === "medium") return "text-amber-700";
    return "text-red-700";
  };

  const renderCitation = (citation, index) => {
    const isUrl = citation.url && citation.url.startsWith("http");

    return (
      <li
        key={citation.id || index}
        className="flex items-start gap-3 py-2 text-sm"
      >
        <span className="mt-0.5 shrink-0 font-mono text-[11px] tabular-nums text-[#94A3B8]">
          [{citation.id || index + 1}]
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-start sm:gap-3">
          <div className="min-w-0 flex-1">
            {isUrl ? (
              <a
                href={citation.url}
                target="_blank"
                rel="noopener noreferrer"
                title={citation.url}
                className="flex min-w-0 items-center gap-1.5 font-medium text-[#020617] underline-offset-2 hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5 shrink-0 text-[#64748B]" aria-hidden="true" />
                <span className="truncate">{citation.filename || citation.url}</span>
              </a>
            ) : (
              <span className="flex min-w-0 items-center gap-1.5 font-medium text-[#334155]">
                <FileText className="h-3.5 w-3.5 shrink-0 text-[#64748B]" aria-hidden="true" />
                <span className="truncate">
                  {citation.filename || t("query.document", "Document")}
                </span>
                {citation.page_number && (
                  <span className="shrink-0 text-xs font-normal text-[#94A3B8]">
                    ({t("query.page", "Page {{count}}", {
                      count: citation.page_number,
                    })}
                    )
                  </span>
                )}
              </span>
            )}
            {citation.heading_path && (
              <p className="mt-0.5 truncate text-xs text-[#94A3B8]">
                {citation.heading_path}
              </p>
            )}
          </div>
          {citation.score && (
            <span className="shrink-0 text-xs tabular-nums text-[#64748B] sm:pt-0.5">
              {t("query.percentMatch", "{{percent}}% match", {
                percent: Math.round(citation.score * 100),
              })}
            </span>
          )}
        </div>
      </li>
    );
  };

  if (botLoading) {
    return (
      <div className="flex items-center justify-center gap-3 py-12">
        <LoadingSpinner size="lg" />
        <span className="text-sm text-[#64748B]">
          {t("query.loadingBot", "Loading bot...")}
        </span>
      </div>
    );
  }

  if (botError || !bot) {
    return (
      <div className="mx-auto max-w-[1400px] space-y-4 pt-2 sm:pt-4">
        <Link to="/bots" className={GRIGIO}>
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          {t("query.backToBots", "Back to Bots")}
        </Link>
        <div className="flex gap-3 rounded-[14px] border border-red-200 bg-red-50 p-4">
          <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
          <div>
            <h3 className="text-sm font-medium text-red-800">
              {t("query.errorLoadingBot", "Error loading bot")}
            </h3>
            <p className="mt-1 text-sm text-red-700">
              {botError?.message || t("query.botNotFound", "Bot not found")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />

      <IntestazioneCasella
        icona={MessageSquare}
        titolo={t("query.interface")}
        dettagli={
          <span className="text-sm text-[#64748B]">
            {t("query.askAbout", { botName: bot.name })}
          </span>
        }
      />

      <div className="pu-fade-up mx-auto grid max-w-[1400px] items-start gap-4 pb-10 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* Le domande e le risposte */}
        <div className="min-w-0 space-y-6">
          {/* Il riquadro per scrivere la domanda */}
          <BrandCard className="p-4 sm:p-5">
            <div className="mb-3">
              <h2 className="text-base font-semibold text-[#020617]">
                {t("query.askQuestion")}
              </h2>
              <p className="mt-0.5 text-xs leading-5 text-[#64748B]">
                {t("query.askQuestionDesc")}
              </p>
            </div>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-3"
              >
                <FormField
                  control={form.control}
                  name="question"
                  rules={{
                    required: t("query.questionRequired"),
                    minLength: {
                      value: 3,
                      message: t("query.questionMinLength"),
                    },
                    maxLength: {
                      value: 1000,
                      message: t("query.questionMaxLength"),
                    },
                  }}
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="sr-only">
                        {t("query.yourQuestion")}
                      </FormLabel>
                      <FormControl>
                        <textarea
                          placeholder={t("query.questionPlaceholder")}
                          className={`${AREA_TESTO} block min-h-[100px] resize-none aria-[invalid=true]:border-red-300`}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs tabular-nums text-[#94A3B8]">
                    {t("query.charactersRemaining", {
                      count: form.watch("question")?.length || 0,
                    })}
                  </span>
                  <button
                    type="submit"
                    className={SCURO}
                    disabled={
                      askQuestion.isPending || !form.watch("question")?.trim()
                    }
                  >
                    {askQuestion.isPending ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                        {t("query.asking")}
                      </>
                    ) : (
                      <>
                        <Send className="h-3.5 w-3.5" aria-hidden="true" />
                        {t("query.askQuestionBtn")}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </Form>
          </BrandCard>

          {/* Le risposte, dalla piu' recente */}
          {queryHistory.length > 0 ? (
            <section className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h2 className="flex items-baseline gap-2 text-sm font-semibold text-[#020617]">
                  {t("query.recentQueries")}
                  <span className="text-xs font-normal tabular-nums text-[#94A3B8]">
                    {queryHistory.length}
                  </span>
                </h2>
                <button
                  type="button"
                  onClick={clearHistory}
                  className={`${BOTTONE} -mr-2 text-[#475569] hover:bg-red-50 hover:text-[#B91C1C]`}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("query.clearHistory")}
                </button>
              </div>

              {queryHistory.map((entry) => (
                <BrandCard key={entry.id} className="pu-carta-in overflow-hidden">
                  <ol className="space-y-4 bg-[#FCFCFD] p-4 sm:p-5">
                    <li className="flex flex-col items-end">
                      <span className="mb-1 flex items-center gap-1 text-[11px] text-[#94A3B8]">
                        {t("query.you", "Tu")}
                        <span aria-hidden="true">·</span>
                        <Clock className="h-3 w-3" aria-hidden="true" />
                        {formatTimestamp(entry.timestamp)}
                      </span>
                      <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-[14px] rounded-br-[4px] bg-[#18181B] px-3.5 py-2.5 text-sm leading-6 text-white">
                        {entry.question}
                      </div>
                    </li>
                    <li className="flex flex-col items-start">
                      <span className="mb-1 flex flex-wrap items-center gap-x-1 text-[11px] text-[#94A3B8]">
                        {t("query.assistantLabel", "Assistente AI")}
                        {entry.confidence && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span
                              className={`font-medium ${getConfidenceColor(
                                entry.confidence
                              )}`}
                            >
                              {t("query.confidence")}: {entry.confidence}
                            </span>
                          </>
                        )}
                      </span>
                      <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-[14px] rounded-bl-[4px] border border-[#E2E8F0] bg-white px-3.5 py-2.5 text-sm leading-6 text-[#0F172A]">
                        {entry.answer}
                      </div>

                      {/* Product Cards */}
                      {entry.cards && entry.cards.length > 0 && (
                        <div className="mt-3 w-full">
                          <ProductCardsGrid cards={entry.cards} />
                        </div>
                      )}
                    </li>
                  </ol>

                  {entry.citations && entry.citations.length > 0 && (
                    <div className="border-t border-[#E2E8F0] px-4 pb-2 pt-3 sm:px-5">
                      <h4 className="flex items-baseline gap-2 text-xs font-medium text-[#334155]">
                        {t("query.citations")}
                        <span className="tabular-nums text-[#94A3B8]">
                          {entry.citations.length}
                        </span>
                      </h4>
                      <ul className="mt-1 divide-y divide-[#F1F5F9]">
                        {entry.citations.map((citation, index) =>
                          renderCitation(citation, index)
                        )}
                      </ul>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2 border-t border-[#E2E8F0] px-4 py-2 sm:px-5">
                    {entry.context && (
                      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[11px] tabular-nums text-[#94A3B8]">
                        <span>
                          {t("query.tokens")}:{" "}
                          {entry.metadata.tokens || "nd"}
                        </span>
                        <span>
                          {t("query.sources")}: {entry.context.sources_used}
                        </span>
                        {entry.context.average_score && (
                          <span>
                            {t("query.avgScore")}:{" "}
                            {Math.round(entry.context.average_score * 100)}%
                          </span>
                        )}
                        {entry.metadata?.processing_time_ms && (
                          <span>
                            {t("query.time")}:{" "}
                            {entry.metadata.processing_time_ms}ms
                          </span>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => saveQuery(entry.question, entry.answer)}
                      className={`${BOTTONE} -mr-2 ml-auto text-[#334155] hover:bg-[#F1F5F9]`}
                    >
                      <BookmarkPlus className="h-3.5 w-3.5" aria-hidden="true" />
                      {t("query.save", "Salva")}
                    </button>
                  </div>
                </BrandCard>
              ))}
            </section>
          ) : (
            <div className="flex flex-col items-center justify-center gap-1.5 rounded-[14px] border border-dashed border-[#CBD5E1] bg-white/60 px-6 py-10 text-center">
              <MessageSquare className="h-4 w-4 text-[#94A3B8]" aria-hidden="true" />
              <p className="text-sm font-medium text-[#334155]">
                {t("query.emptyTitle", "Le risposte compaiono qui")}
              </p>
              <p className="max-w-sm text-xs leading-5 text-[#64748B]">
                {t(
                  "query.emptyHint",
                  "Scrivi una domanda come la farebbe un cliente: vedrai cosa risponde l'assistente e da quali fonti ha preso le informazioni."
                )}
              </p>
            </div>
          )}
        </div>

        {/* Di lato: le domande salvate e l'assistente */}
        <div className="min-w-0 space-y-4 lg:sticky lg:top-4">
          <BrandCard className="overflow-hidden">
            <div className="border-b border-[#E2E8F0] px-4 py-3">
              <h2 className="text-sm font-semibold text-[#020617]">
                {t("query.savedQueries")}
              </h2>
              <p className="mt-0.5 text-xs leading-5 text-[#64748B]">
                {t("query.savedQueriesDesc")}
              </p>
            </div>
            {savedQueries.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs leading-5 text-[#94A3B8]">
                {t("query.noSavedQueries")}
              </p>
            ) : (
              <ul className="divide-y divide-[#F1F5F9]">
                {savedQueries.slice(0, 10).map((query) => (
                  <li key={query.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-[#020617]">
                          {query.question}
                        </p>
                        <p className="mt-0.5 text-[11px] text-[#94A3B8]">
                          {formatTimestamp(query.timestamp)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSavedQuery(query.id)}
                        aria-label={t("query.removeSaved", "Togli dai salvati")}
                        title={t("query.removeSaved", "Togli dai salvati")}
                        className="-mr-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] text-[#94A3B8] transition-colors hover:bg-red-50 hover:text-[#B91C1C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </div>
                    <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-[#64748B]">
                      {query.answer}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </BrandCard>

          <BrandCard className="overflow-hidden">
            <h2 className="border-b border-[#E2E8F0] px-4 py-3 text-sm font-semibold text-[#020617]">
              {t("query.botInformation")}
            </h2>
            <dl className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-3 gap-y-2.5 px-4 py-3 text-sm">
              <dt className="text-xs font-medium leading-5 text-[#64748B]">
                {t("query.name")}
              </dt>
              <dd className="min-w-0 break-words text-[#0F172A]">{bot.name}</dd>
              {bot.description && (
                <>
                  <dt className="text-xs font-medium leading-5 text-[#64748B]">
                    {t("query.description")}
                  </dt>
                  <dd className="min-w-0 break-words text-[#334155]">
                    {bot.description}
                  </dd>
                </>
              )}
              <dt className="text-xs font-medium leading-5 text-[#64748B]">
                {t("query.sourcesCount")}
              </dt>
              <dd className="tabular-nums text-[#0F172A]">{fonti}</dd>
              <dt className="text-xs font-medium leading-5 text-[#64748B]">
                {t("query.status")}
              </dt>
              <dd>
                <StatoAssistente stato={bot.status} />
              </dd>
            </dl>
          </BrandCard>
        </div>
      </div>
    </div>
  );
};

export default QueryInterface;
